import {
  LookBackDate,
  LookBackOptions,
  RiskPatternItem,
  SelectOption
} from "@/rca-types";
import { getRelativeDate } from "@/utils/functions";
import {
  ICaseCountry,
  ICaseReasonCode,
  ICaseStore
} from "@/types/casesFrontendTypes";

// Define the module state interface
export interface CaseFilterState {
  search: string;
  riskLevel: string[] | null;
  riskPatterns: RiskPatternItem[] | null;
  reasonCodes: ICaseReasonCode[] | null;
  caseStatus: SelectOption[] | null;
  overdue: boolean;
  countries: ICaseCountry[] | null;
  assignedTo: string;
  lookBackTextValue: LookBackOptions;
  lookBackDateValue: LookBackDate | null;
  today: Date;
  stores: ICaseStore[] | null;
  currentStores: string[] | null;
}

const state = (): CaseFilterState => {
  const defaultLookBackTextValue = "1 year" as LookBackOptions;
  const latestDate = new Date();

  return {
    search: "",
    riskLevel: null as string[] | null,
    riskPatterns: null as RiskPatternItem[] | null,
    reasonCodes: null as ICaseReasonCode[] | null,
    caseStatus: null as SelectOption[] | null,
    overdue: false,
    countries: null as ICaseCountry[] | null,
    assignedTo: "",
    lookBackTextValue: defaultLookBackTextValue,
    lookBackDateValue: getRelativeDate(defaultLookBackTextValue, latestDate),
    today: latestDate,
    stores: null as ICaseStore[] | null,
    currentStores: null as string[] | null
  };
};

const mutations = {
  SET_SEARCH(state: CaseFilterState, searchEntry: string) {
    state.search = searchEntry;
  },
  SET_RISK_LEVEL(state: CaseFilterState, entry: string[]) {
    state.riskLevel = entry;
  },
  SET_RISK_PATTERNS(state: CaseFilterState, entry: RiskPatternItem[]) {
    state.riskPatterns = entry;
  },
  SET_REASON_CODES(state: CaseFilterState, entry: ICaseReasonCode[]) {
    state.reasonCodes = entry;
  },
  SET_CASE_STATUS(state: CaseFilterState, entry: SelectOption[]) {
    state.caseStatus = entry;
  },
  SET_OVERDUE(state: CaseFilterState, entry: boolean) {
    state.overdue = entry;
  },
  SET_COUNTRIES(state: CaseFilterState, entry: ICaseCountry[]) {
    state.countries = entry;
  },
  SET_ASSIGNED_TO(state: CaseFilterState, entry: string) {
    state.assignedTo = entry;
  },
  SET_LOOK_BACK_FILTER(
    state: CaseFilterState,
    payload: {
      textValue: LookBackOptions;
      dateValue: LookBackDate;
      today: Date;
    }
  ) {
    state.lookBackTextValue = payload.textValue;
    state.lookBackDateValue = payload.dateValue;
    state.today = payload.today;
  },
  SET_STORES(state: CaseFilterState, entry: ICaseStore[]) {
    state.stores = entry;
  },
  SET_CURRENT_STORES(state: CaseFilterState, entry: string[]) {
    state.currentStores = entry;
  }
};

const actions = {
  async SEARCH_FILTER({ commit }: any, searchEntry: string) {
    commit("SET_SEARCH", searchEntry);
  },
  async RISK_LEVEL_FILTER({ commit }: any, entry: string[]) {
    commit("SET_RISK_LEVEL", entry);
  },
  async RISK_PATTERN_FILTER({ commit }: any, entry: RiskPatternItem[]) {
    commit("SET_RISK_PATTERNS", entry);
  },
  async REASON_CODE_FILTER({ commit }: any, entry: ICaseReasonCode[]) {
    commit("SET_REASON_CODES", entry);
  },
  async CASE_STATUS_FILTER({ commit }: any, entry: SelectOption[]) {
    commit("SET_CASE_STATUS", entry);
  },
  async CASE_OVERDUE_FILTER({ commit }: any, entry: boolean) {
    commit("SET_OVERDUE", entry);
  },
  async COUNTRY_FILTER({ commit }: any, entry: ICaseCountry[]) {
    commit("SET_COUNTRIES", entry);
  },
  async ASSIGNMENT_FILTER({ commit }: any, entry: string) {
    commit("SET_ASSIGNED_TO", entry);
  },
  async LOOK_BACK_FILTER({ commit }: any, textValue: LookBackOptions) {
    const latestDate = new Date();
    const dateValue = getRelativeDate(textValue, latestDate);

    commit("SET_LOOK_BACK_FILTER", {
      textValue,
      dateValue,
      today: latestDate
    });
  },
  async STORES_FILTER({ commit }: any, entry: ICaseStore[]) {
    commit("SET_STORES", entry);
  },
  async CURRENT_STORES({ commit }: any, entry: string[]) {
    commit("SET_CURRENT_STORES", entry);
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  actions
};
